import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Paper, useTheme, useMediaQuery } from '@mui/material';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import SpotlightStep from './SpotlightStep';
import type { SpotlightStepConfig } from '../../types';

const MotionPaper = motion.create(Paper);

const VIEWPORT_MARGIN = 16;
const TOOLTIP_MAX_WIDTH = 320;
const TOOLTIP_GAP = 12;

interface TooltipPosition {
  top: number;
  left: number;
}

interface SpotlightTooltipProps {
  step: SpotlightStepConfig;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onDismiss: () => void;
}

function findTarget(step: SpotlightStepConfig): Element | null {
  return (
    document.querySelector(step.targetSelector) ||
    (step.targetFallback ? document.querySelector(step.targetFallback) : null)
  );
}

function computePosition(
  targetRect: DOMRect,
  tooltipRect: { width: number; height: number },
  preferredPosition: SpotlightStepConfig['tooltipPosition']
): TooltipPosition {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tw = tooltipRect.width;
  const th = tooltipRect.height;

  // Determine position (auto logic if not specified)
  let position = preferredPosition || 'auto';
  if (position === 'auto') {
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const targetCenterX = targetRect.left + targetRect.width / 2;

    if (targetCenterX > vw - 200) {
      position = 'left';
    } else if (targetCenterX < 200) {
      position = 'right';
    } else if (targetCenterY > vh / 2) {
      position = 'top';
    } else {
      position = 'bottom';
    }
  }

  let top = 0;
  let left = 0;

  switch (position) {
    case 'left':
      top = targetRect.top + targetRect.height / 2 - th / 2;
      left = targetRect.left - tw - TOOLTIP_GAP;
      break;
    case 'right':
      top = targetRect.top + targetRect.height / 2 - th / 2;
      left = targetRect.right + TOOLTIP_GAP;
      break;
    case 'top':
      top = targetRect.top - th - TOOLTIP_GAP;
      left = targetRect.left + targetRect.width / 2 - tw / 2;
      break;
    case 'bottom':
      top = targetRect.bottom + TOOLTIP_GAP;
      left = targetRect.left + targetRect.width / 2 - tw / 2;
      break;
  }

  // Clamp within viewport with margin
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - th - VIEWPORT_MARGIN));
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - tw - VIEWPORT_MARGIN));

  return { top, left };
}

const SpotlightTooltip: React.FC<SpotlightTooltipProps> = ({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onDismiss,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shouldReduceMotion = useReducedMotion();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
  const [ready, setReady] = useState(false);

  const updatePosition = useCallback(() => {
    const el = findTarget(step);
    if (!el || !tooltipRef.current || isMobile) return;

    const targetRect = el.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const pos = computePosition(targetRect, tooltipRect, step.tooltipPosition);
    setPosition(pos);
    setReady(true);
  }, [step, isMobile]);

  // Position on mount and when step changes
  useEffect(() => {
    // Small delay to ensure tooltip has rendered and has dimensions
    const frame = requestAnimationFrame(() => {
      updatePosition();
    });
    return () => cancelAnimationFrame(frame);
  }, [updatePosition, stepIndex]);

  // Reposition on resize
  useEffect(() => {
    if (isMobile) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(updatePosition, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [updatePosition, isMobile]);

  // Mobile bottom-sheet style
  if (isMobile) {
    return (
      <AnimatePresence mode="wait">
        <MotionPaper
          key={stepIndex}
          elevation={8}
          initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={shouldReduceMotion ? undefined : { opacity: 0, y: 20, transition: { duration: 0.15 } }}
          transition={
            shouldReduceMotion
              ? { duration: 0 }
              : { type: 'spring', damping: 20, stiffness: 300, delay: 0.15 }
          }
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1500,
            borderRadius: '16px 16px 0 0',
            p: 2.5,
            pb: 3,
            maxHeight: '60vh',
            overflow: 'auto',
          }}
        >
          <SpotlightStep
            step={step}
            stepIndex={stepIndex}
            totalSteps={totalSteps}
            onNext={onNext}
            onDismiss={onDismiss}
          />
        </MotionPaper>
      </AnimatePresence>
    );
  }

  // Desktop floating tooltip
  return (
    <AnimatePresence mode="wait">
      <MotionPaper
        ref={tooltipRef}
        key={stepIndex}
        elevation={8}
        initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.9, y: -10 }}
        animate={{
          opacity: ready ? 1 : 0,
          scale: 1,
          y: 0,
        }}
        exit={
          shouldReduceMotion
            ? undefined
            : { opacity: 0, y: 10, transition: { duration: 0.15 } }
        }
        transition={
          shouldReduceMotion
            ? { duration: 0 }
            : { type: 'spring', damping: 20, stiffness: 300, delay: 0.15 }
        }
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          zIndex: 1500,
          maxWidth: TOOLTIP_MAX_WIDTH,
          width: TOOLTIP_MAX_WIDTH,
          borderRadius: '12px',
          p: 2.5,
          pointerEvents: 'auto',
        }}
      >
        <SpotlightStep
          step={step}
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          onNext={onNext}
          onDismiss={onDismiss}
        />
      </MotionPaper>
    </AnimatePresence>
  );
};

export default SpotlightTooltip;
